// Telling the server which clock she is on.
//
// This is the ONLY place in the client that asks the browser for a timezone,
// and it is deliberately separate from clocks.js and from the picker. The
// distinction matters:
//
//   - Her date and time picks are interpreted using the location she TAPS.
//     A browser zone is wrong in exactly the cases that matter there — an
//     airport, a VPN, a phone that has not caught up after a flight — and a
//     mis-detected zone silently booking a date at the wrong hour is a much
//     worse failure than a badly timed water reminder.
//   - The hydration reminders have to be scheduled on some clock even when
//     she is not looking at the page, and there is nothing else to ask.
//     Discord does not expose a user's timezone.
//
// The server accepts only the zones she can actually be in, so a wrong answer
// here is discarded rather than acted on.

import { CREDENTIAL_HEADER, readCredential } from './credential.js';

/**
 * Report the browser's timezone, if it will say.
 *
 * Fire-and-forget: nothing on the page depends on the outcome, and a failure
 * must not be visible to her. Returns the promise only so tests can wait.
 *
 * @param {object} [options]
 * @param {typeof fetch} [options.fetchImpl]
 * @param {() => string | undefined} [options.readZone]
 * @returns {Promise<void>}
 */
export function reportZone({
  fetchImpl = fetch,
  readZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone,
} = {}) {
  let timeZone;
  try {
    timeZone = readZone();
  } catch {
    return Promise.resolve();
  }
  if (typeof timeZone !== 'string' || timeZone.length === 0) return Promise.resolve();

  return fetchImpl('/api/here', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [CREDENTIAL_HEADER]: readCredential() ?? '',
    },
    body: JSON.stringify({ timeZone }),
  }).then(() => undefined).catch(() => undefined);
}
